import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DateInputFr } from "@/components/DateInputFr";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEur } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Tag, Layers } from "lucide-react";
import { toast } from "sonner";

const empty = { start_date: "", cee_price: "", cee_price_precaire: "", label: "", tiers: [] };
const num = (v) => (v === "" || v == null ? null : parseFloat(v));
const fmtMw = (v) => Number(v).toLocaleString("fr-FR");

export default function PriceSchedule() {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [scope, setScope] = useState("genis");
  const [partners, setPartners] = useState([]);
  const params = scope === "genis" ? {} : { partner_id: scope };

  const load = () => api.get("/price-schedule", { params }).then((r) => setEntries(r.data));
  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin) api.get("/admin/partners").then((r) => setPartners(r.data)); }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      start_date: e.start_date, cee_price: e.cee_price, cee_price_precaire: e.cee_price_precaire ?? "", label: e.label || "",
      tiers: (e.tiers || []).map((t) => ({ key: crypto.randomUUID(), min_mwhc: t.min_mwhc, cee_price: t.cee_price, cee_price_precaire: t.cee_price_precaire ?? "" })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.start_date || form.cee_price === "") {
      toast.error("Date de début et prix classique requis");
      return;
    }
    if (form.tiers.some((t) => t.min_mwhc === "" || t.cee_price === "" || parseFloat(t.min_mwhc) <= 0)) {
      toast.error("Chaque palier doit avoir un seuil > 0 MWhc et un prix classique");
      return;
    }
    setSaving(true);
    try {
      const body = {
        start_date: form.start_date,
        cee_price: parseFloat(form.cee_price),
        cee_price_precaire: form.cee_price_precaire === "" ? null : parseFloat(form.cee_price_precaire),
        label: form.label,
        tiers: form.tiers.map((t) => ({ min_mwhc: num(t.min_mwhc), cee_price: num(t.cee_price), cee_price_precaire: num(t.cee_price_precaire) })),
      };
      if (editing) await api.put(`/price-schedule/${editing.id}`, body);
      else await api.post("/price-schedule", body, { params });
      toast.success(editing ? "Tarif mis à jour" : "Tarif ajouté");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/price-schedule/${id}`);
      toast.success("Tarif supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const setTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { key: crypto.randomUUID(), min_mwhc: "", cee_price: "", cee_price_precaire: "" }] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, j) => j !== i) }));

  if (user?.partner) return <Navigate to="/" replace />;

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-genis">Interne</p>
          <h1 className="text-3xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">Barème de prix CEE</h1>
          <p className="text-muted-foreground mt-1">Définissez le prix des CEE (classique et précarité) par date d'engagement, avec des paliers de volume par dossier. Le tarif du palier atteint s'applique à tout le volume du dossier.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-72" data-testid="price-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="genis">Barème Genis Energie</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`price-scope-${p.slug}`}>Barème {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={openNew} className="bg-genis hover:bg-genis-dark text-white" data-testid="add-price-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Ajouter un tarif
            </Button>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        {!entries ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-7 w-7 animate-spin text-genis" /></div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <Tag className="h-10 w-10 mx-auto mb-3 opacity-40" />
            Aucun tarif enregistré. Ajoutez votre premier barème.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="price-table">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <th className="px-4 py-3 font-semibold">Applicable à partir du</th>
                <th className="px-4 py-3 font-semibold">Libellé</th>
                <th className="px-4 py-3 font-semibold text-right">Prix classique</th>
                <th className="px-4 py-3 font-semibold text-right">Prix précarité</th>
                <th className="px-4 py-3 font-semibold">Paliers de volume</th>
                {isAdmin && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border" data-testid={`price-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium">{fmtDate(e.start_date)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.label || "—"}</td>
                  <td className="px-4 py-3 text-right metric">{e.cee_price} €/MWhc</td>
                  <td className="px-4 py-3 text-right metric text-indigo-700">{e.cee_price_precaire != null ? `${e.cee_price_precaire} €/MWhc` : "—"}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`price-tiers-${e.id}`}>
                    {(e.tiers || []).length === 0 ? <span className="text-muted-foreground">Prix unique</span> : (
                      <div className="space-y-0.5">
                        {e.tiers.map((t) => (
                          <div key={t.min_mwhc} className="metric">≥ {fmtMw(t.min_mwhc)} MWhc : {t.cee_price} €{t.cee_price_precaire != null && <span className="text-indigo-700"> / {t.cee_price_precaire} €</span>}</div>
                        ))}
                      </div>
                    )}
                  </td>
                  {isAdmin && <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(e)} data-testid={`edit-price-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(e.id)} data-testid={`delete-price-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="price-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le tarif" : "Ajouter un tarif"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Applicable à partir du *</Label>
              <DateInputFr value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} testid="price-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Libellé (optionnel)</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Barème T3 2026" data-testid="price-label" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Palier de base · dès 0 MWhc</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prix classique (€/MWhc) *</Label>
                <Input type="number" step="0.01" value={form.cee_price} onChange={(e) => setForm({ ...form, cee_price: e.target.value })} placeholder="8.71" data-testid="price-classique" />
              </div>
              <div className="space-y-1.5">
                <Label>Prix précarité (€/MWhc)</Label>
                <Input type="number" step="0.01" value={form.cee_price_precaire} onChange={(e) => setForm({ ...form, cee_price_precaire: e.target.value })} placeholder="Optionnel" data-testid="price-precaire" />
              </div>
            </div>
            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="price-tiers-editor">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-genis" />
                <span className="text-sm font-semibold">Paliers de volume supplémentaires</span>
                <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={addTier} data-testid="add-tier-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un palier
                </Button>
              </div>
              {form.tiers.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun palier : le prix de base s'applique quel que soit le volume du dossier.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[11px] font-semibold text-muted-foreground">
                    <span>À partir de (MWhc)</span><span>Prix classique (€)</span><span>Prix précarité (€)</span><span className="w-8" />
                  </div>
                  {form.tiers.map((t, i) => (
                    <div key={t.key} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`tier-row-${i}`}>
                      <Input type="number" step="1" value={t.min_mwhc} onChange={(e) => setTier(i, "min_mwhc", e.target.value)} placeholder="1000" data-testid={`tier-min-${i}`} />
                      <Input type="number" step="0.01" value={t.cee_price} onChange={(e) => setTier(i, "cee_price", e.target.value)} placeholder="8.50" data-testid={`tier-price-${i}`} />
                      <Input type="number" step="0.01" value={t.cee_price_precaire} onChange={(e) => setTier(i, "cee_price_precaire", e.target.value)} placeholder="Optionnel" data-testid={`tier-precaire-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-8 text-destructive" onClick={() => removeTier(i)} data-testid={`tier-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <p className="text-[11px] text-muted-foreground">Le prix du palier atteint par le volume total du dossier s'applique à tout le dossier.</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-genis hover:bg-genis-dark text-white" data-testid="save-price-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
